import _ from 'underscore';
import { ColumnsConfigBuilder } from '@bingads-webui-campaign/summary-grid-helper';
import {
  ColumnClicks,
  ColumnImpressions,
  ColumnCTR,
  ColumnAverageCPC,
  ColumnAveragePosition,
  ColumnSpend,
  ColumnAverageCPM,
  ColumnTopImpressionRate,
  ColumnAbsoluteTopImpressionRate,
} from '@bingads-webui-campaign/campaignui-common-columns';
import { criterionCashbackPercentColumnFactory } from '@bingads-webui-campaign/personalized-offer-columns';
import ColumnAdGroup from './columns/adgroup-column';
import ColumnAdGroupId from './columns/adgroup-id-column';
import ColumnBidAdjustment from './columns/bid-adjustment-column';
import ColumnCampaign from './columns/campaign-column';
import ColumnCampaignId from './columns/campaign-id-column';
import ColumnCampaignType from './columns/campaign-type-column';
import ColumnLevel from './columns/level-column';
import { TargetingSettingsContext } from './targeting-settings-context';
import { TargetingScope, TargetingLevel, TabItems } from './config';

const helpIdMap = {
  Clicks: 'Page_CampaignGrids_Clicks',
  Impressions: 'Page_CampaignGrids_Impr',
  CTR: 'Page_CampaignGrids_CTR',
  AverageCPC: 'Page_CampaignGrids_AvgCPC',
  AveragePosition: 'Page_CampaignGrids_AvgPosition',
  Spend: 'Page_CampaignGrids_Spend',
  AverageCPM: 'Page_CampaignGrids_AvgCPM',
  CPA: 'Page_CampaignGrids_CPA',
  Conversions: 'Page_CampaignGrids_Conv',
  ConversionRate: 'Page_CampaignGrids_ConvRate',
  AdvertiserReportedRevenue: 'Page_CampaignGrids_Revenue',
  RevenueOnAdSpend: 'Page_CampaignGrids_ReturnAdSpend',
  ViewThroughConversions: 'pop_BA_CampaignGrids_ViewThrough',
  ViewThroughConversionsRevenue: 'pop_BA_Reporting_Clarity_ViewThroughConvRevenue',
  ViewThroughConversionsCPA: 'pop_BA_Reporting_Clarity_ViewThroughConvCPA',
  ViewThroughConversionsReturnOnAdSpend: 'pop_BA_Reporting_Clarity_ViewThroughConvReturnOnAdSpend',
  ViewThroughConversionsRate: 'pop_BA_Reporting_Clarity_ViewThroughConvRate',
  AllConversionCPA: 'pop_BA_CampaignGrids_AllConvCPA',
  AllConversions: 'pop_BA_CampaignGrids_AllConv',
  AllConversionAdvertiserReportedRevenue: 'pop_BA_CampaignGrids_AllConvRevenue',
  AllConversionRevenueOnAdSpend: 'pop_BA_CampaignGrids_AllConvROAS',
  AllConversionRate: 'pop_BA_CampaignGrids_AllConvRate',
  // Partial Conversion
  ConversionsCredit: 'Page_CampaignGrids_Conv',
  PartialConversionCPA: 'Page_CampaignGrids_CPA',
  PartialConversionRate: 'Page_CampaignGrids_ConvRate',
  AllPartialConversionCPA: 'pop_BA_CampaignGrids_AllConvCPA',
  AllConversionsCredit: 'pop_BA_CampaignGrids_AllConv',
  AllPartialConversionRate: 'pop_BA_CampaignGrids_AllConvRate',
  ViewThroughPartialConversionsCPA: 'pop_BA_Reporting_Clarity_ViewThroughConvCPA',
  ViewThroughPartialConversionsRate: 'pop_BA_Reporting_Clarity_ViewThroughConvRate',
  ViewThroughConversionsCredit: 'pop_BA_CampaignGrids_ViewThrough',
};

export class ColumnsConfig {
  constructor(deps) {
    const {
      selfColumns,
      scope,
      level,
      parentView,
      isExcludeLocation = false,
    } = deps;
    const {
      IsViewThroughConversionEnabled,
      IsAvgPositionDeprecationPhase1Enabled,
      IsAvgPositionDeprecationPhase2Enabled,
      IsPersonalizedOffersEnabled,
      IsPersonalizedOffersV3Enabled,
      IsViewThroughConversionsRevenueEnabled,
      dynamic = {},
    } = TargetingSettingsContext.pageContext.Permissions;
    const {
      PartialConversions: IsPartialConversionsEnabled,
      ColumnConversion: IsColumnConversionEnabled,
    } = dynamic;
    const usePartialConversions = IsPartialConversionsEnabled && IsColumnConversionEnabled;

    let columns = [];
    const otherAttrColumns = [];

    this.scope = scope;
    this.level = level;
    this.parentView = parentView;

    if (_.isArray(selfColumns) && selfColumns.length > 0) {
      columns = [].concat(selfColumns);
    }

    if (scope !== TargetingScope.AdGroup) {
      if (scope !== TargetingScope.Campaign) {
        columns.push(ColumnCampaign);
        otherAttrColumns.push(ColumnCampaignType);
        otherAttrColumns.push(ColumnCampaignId);
      }
      if (level === TargetingLevel.AdGroup) {
        columns.push(ColumnAdGroup);
        otherAttrColumns.push(ColumnAdGroupId);
      }
    }

    if (level === TargetingLevel.AdGroup) {
      otherAttrColumns.push(ColumnLevel);
    }

    if (!isExcludeLocation) {
      columns.push(ColumnBidAdjustment);

      if (IsPersonalizedOffersEnabled && !IsPersonalizedOffersV3Enabled) {
        const criterionLevel = level === 0 ? 'Campaign' : 'AdGroup';
        const CashbackPercentColumn = criterionCashbackPercentColumnFactory({
          criterionLevel,
          criterionCashbackUpdater: (view, item, model) => {
            item.CriterionCashback['@odata.type'] = 'Model.CashbackAdjustment'; // eslint-disable-line
            view.grid.gridView.trigger('didEdit', model, item, 'CashbackPercent');
          },
          criterionCashbackOverrideUpdater: (view, item) => {
            view.grid.gridView.trigger('inlineEdit', item);
          },
          modelName: `Model.Biddable${criterionLevel}Criterion`,
          decimal: TargetingSettingsContext.decimal,
        });
        columns.push(CashbackPercentColumn);
      }
    }
    columns = _.compact(columns);

    this.builder = new ColumnsConfigBuilder()
      .addCategory('Attributes', category => category.addColumns(_.union(columns, otherAttrColumns)));

    if (!isExcludeLocation) {
      this.builder
        .addCategory('Performance', category => category.addColumns(_.compact([
          ColumnClicks,
          ColumnImpressions,
          ColumnCTR,
          ColumnAverageCPC,
          ColumnSpend,
          !IsAvgPositionDeprecationPhase2Enabled && ColumnAveragePosition,
          IsAvgPositionDeprecationPhase1Enabled && ColumnTopImpressionRate,
          IsAvgPositionDeprecationPhase1Enabled && ColumnAbsoluteTopImpressionRate,
          ColumnAverageCPM,
        ])))
        .addConversionsCategory({
          IsViewThroughConversionEnabled,
          IsViewThroughConversionsRevenueEnabled,
          withPartialConversions: IsPartialConversionsEnabled,
          IsColumnConversionEnabled: usePartialConversions,
        });
    }

    this.builder
      .setLockedColumns(columns)
      .setSelectedColumns(_.compact(columns.concat(_.compact([
        ColumnClicks,
        ColumnImpressions,
        ColumnCTR,
        ColumnAverageCPC,
        !IsAvgPositionDeprecationPhase1Enabled
          && !IsAvgPositionDeprecationPhase2Enabled
          && ColumnAveragePosition,
        IsAvgPositionDeprecationPhase1Enabled && ColumnTopImpressionRate,
        IsAvgPositionDeprecationPhase1Enabled && ColumnAbsoluteTopImpressionRate,
        ColumnSpend]))));
  }

  getColumnsConfig(tabName, customValidators, isExcludeLocation = false) {
    const columnHelpId = name => (_.isUndefined(helpIdMap[name]) ? `Page_CampaignGrids_${name}` : helpIdMap[name]);
    const gridType = _.findWhere(TabItems, { name: tabName }).routeParams.setting;

    return this.builder.getColumnsConfig({
      i18n: TargetingSettingsContext.i18n,
      gridType,
      isExcludeLocation,
      scope: this.scope,
      level: this.level,
      parentView: this.parentView,
      view: this.parentView,
      customValidators,
      helpId: columnHelpId,
      isForVNext: true,
    });
  }

  getColumnChooserConfig() {
    const mandatoryProperties = ['Id'];

    if (this.level === TargetingLevel.AdGroup) {
      Array.prototype.push.apply(mandatoryProperties, ['AdGroupId', 'LevelOfDetails']);
    }
    return _.defaults(this.builder.getColumnChooserConfig({
      mandatoryProperties,
      i18n: TargetingSettingsContext.i18n,
      patchQuery: true,
      Columns: TargetingSettingsContext.Columns,
      SelectedColumns: TargetingSettingsContext.SelectedColumns,
      LegacyColumns: TargetingSettingsContext.LegacyColumns,
      LegacySelectedColumns: TargetingSettingsContext.LegacySelectedColumns,
      permissions: TargetingSettingsContext.pageContext.Permissions,
    }), { isForVNext: true });
  }
}
